'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';

import type { AskQueryFormValues } from '@ui/components/features/landing/forms/AskQueryForm';
import { BlockedAccountBanner as BlockedAccountBannerUI } from '@ui/components/features/profile/blockedAccountBanner';
import { ContactSupportDialog } from '@ui/components/features/support';
import { Button } from '@ui/components/ui/button';
import { useCurrentUser } from '@website/hooks/useCurrentUser';
import { BrowserApiClient } from '@website/lib/api/browser';
import { collectClientContext } from '@website/utils/collectClientContext';

/**
 * Sitewide alert surfaced under the header for any blocked account:
 *
 * - Professionals blocked via `user.professionalBlockedAt` (admin action or
 *   auto-block after 3 consecutive cancellations without a completed session).
 * - Learners blocked via `user.blockedAt` (admin action). Blocked learners
 *   cannot create new bookings.
 *
 * Visibility rules:
 *
 * - Only shown when the user is authenticated AND their own account is
 *   blocked. The backend (`/session`) surfaces these fields only for the user
 *   themselves, so this banner can never be triggered for someone else.
 * - Always visible across every page — intentional so the account holder
 *   cannot miss the impact on their ability to transact on the platform.
 *
 * The "Contact support" CTA opens the shared support dialog (same form as
 * the FAQ page) so the user can reach support directly from the banner.
 */
export function BlockedAccountBanner() {
  const t = useTranslations('BlockedAccountBanner');
  const tSupport = useTranslations('ContactSupport');
  const { data: user } = useCurrentUser();
  const [isSupportOpen, setSupportOpen] = useState(false);

  const isBlocked = !!user?.professionalBlockedAt || !!user?.blockedAt;
  if (!isBlocked) return null;

  // Professional block takes precedence in its reason wording — if both are
  // set (unusual but possible if a pro was also blocked as a user), we keep
  // the pro reason so the user sees the most specific context.
  const reason = user?.professionalBlockReason ?? user?.blockReason;

  const translations = {
    title: t('title'),
    fallbackDescription: t('fallbackDescription'),
    reasonLabel: t('reasonLabel')
  };

  const handleSubmitSupport = async (data: AskQueryFormValues, attachments?: File[]) => {
    const attachmentKeys: string[] = [];

    if (attachments && attachments.length > 0) {
      for (const file of attachments) {
        const uploadResponse = await BrowserApiClient.getPrivateAssetUploadUrl({
          filename: file.name,
          type: file.type
        });

        const uploadResult = await fetch(uploadResponse.uploadUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type }
        });

        if (!uploadResult.ok) {
          throw new Error('Failed to upload attachment');
        }

        attachmentKeys.push(uploadResponse.key);
      }
    }

    await BrowserApiClient.sendQuery({
      ...(data.email && { email: data.email }),
      message: data.question,
      attachments: attachmentKeys,
      clientContext: collectClientContext()
    });
  };

  return (
    <>
      <BlockedAccountBannerUI
        translations={translations}
        reason={reason}
        ctaSlot={
          <Button size="sm" variant="destructive" onClick={() => setSupportOpen(true)}>
            {t('supportCta')}
          </Button>
        }
      />

      <ContactSupportDialog
        isOpen={isSupportOpen}
        onClose={() => setSupportOpen(false)}
        defaultEmail={user?.email}
        onSubmit={handleSubmitSupport}
        translations={{
          title: tSupport('title'),
          email: tSupport('email'),
          emailPlaceholder: tSupport('emailPlaceholder'),
          question: tSupport('question'),
          questionPlaceholder: tSupport('questionPlaceholder'),
          attachment: tSupport('attachment'),
          attachmentPrompt: tSupport('attachmentPrompt'),
          submitButton: tSupport('submitButton'),
          submittingButton: tSupport('submittingButton'),
          responseTime: tSupport('responseTime'),
          successMessage: tSupport('successMessage'),
          successDescription: tSupport('successDescription'),
          errorMessage: tSupport('errorMessage'),
          emailError: tSupport('emailError'),
          questionError: tSupport('questionError')
        }}
      />
    </>
  );
}
